import { noop } from "lodash-es";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Agreement, PermissionName } from "../../../clients/graphqlTypes.js";
import { Stack } from "../../commons/layout/Stack.js";
import { REQUIREMENT_ITEM_TYPES } from "../../constants.js";
import { BareTreeItem } from "../../trees/BareTreeItem.js";
import { TreeRightIcons } from "../../trees/components/TreeRightIcons.js";
import { BuildingPlacementBox } from "../doc-items/boxes/BuildingPlacementBox.js";
import { DecisionManagementBox } from "../doc-items/boxes/DecisionManagementBox.js";
import { HeaderBox } from "../doc-items/boxes/HeaderBox.js";
import { ItemFilesBox } from "../doc-items/boxes/ItemFilesBox.js";
import { QuantityBox } from "../doc-items/boxes/QuantityBox.js";
import { RequiredOnCompletionBox } from "../doc-items/boxes/RequiredOnCompletionBox.js";
import {
  SubelementsBox,
  SubitemComponentProps,
} from "../doc-items/boxes/SubelementsBox.js";
import { getData123 } from "../projects/requirement-trees.js";
import {
  RequirementItem_ChildItemComponentFragment,
  RequirementItem_ChildItemFragment,
  RequirementItem_ItemFragment,
  RequirementItem_ProjectFragment,
  RequirementItem_RequirementFragment,
} from "./RequirementItem.generated.js";
import { shouldRenderRequirementCreateInput } from "./helpers.js";
import { RulesBox } from "../doc-items/boxes/RulesBox.js";

interface Props {
  project: RequirementItem_ProjectFragment;
  requirement: RequirementItem_RequirementFragment;
  item: RequirementItem_ItemFragment;
  onUpdateDataRefetchQueries?: string[];
  pathToDocPage: string;
}

export const RequirementItem = ({
  project,
  requirement,
  item,
  onUpdateDataRefetchQueries,
  pathToDocPage,
}: Props) => {
  const navigate = useNavigate();
  const agreement: Agreement = "NONE";

  const ChildItemComponent = React.useMemo(
    () =>
      agreement !== undefined
        ? createChildItemComponent(
            pathToDocPage,
            { viewerPermissions: project.viewerPermissions },
            agreement
          )
        : () => null,
    [pathToDocPage, project.viewerPermissions]
  );

  return (
    <Stack flexDirection="column">
      <HeaderBox
        projectId={project.id}
        docId={requirement.id}
        docType="REQUIREMENT"
        item={item}
        viewMode={"edit"}
        setViewMode={noop}
        onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
        documentViewerPermissions={requirement.viewerPermissions}
      />

      {!item.isRootItem && item.type !== "unpaid" && (
        <RulesBox
          projectId={project.id}
          docId={requirement.id}
          item={item}
          canEdit={true}
          docType="REQUIREMENT"
          documentViewerPermissions={requirement.viewerPermissions}
        />
      )}

      <QuantityBox item={item} docId={requirement.id} projectId={project.id} />

      {item.type === "decision" && (
        <DecisionManagementBox<RequirementItem_ChildItemFragment>
          item={item}
          doc={{
            id: requirement.id,
            docType: "REQUIREMENT",
            templateIsDraft: false,
            organisationId: requirement.owningSystemOrganisationId,
          }}
          projectId={project.id}
          onItemDeleted={() => {
            navigate(pathToDocPage, { replace: true });
          }}
        />
      )}

      <RequiredOnCompletionBox
        project={project}
        docId={requirement.id}
        document={requirement}
        item={item}
        canEdit={true}
        onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
      />

      <ItemFilesBox
        projectId={project.id}
        docId={requirement.id}
        docType="REQUIREMENT"
        viewerPermissions={requirement.viewerPermissions}
        item={item}
        onUpdateDataRefetchQueries={onUpdateDataRefetchQueries}
      />

      <SubelementsBox<RequirementItem_ChildItemFragment>
        projectId={project.id}
        doc={{
          id: requirement.id,
          docType: "REQUIREMENT",
          templateIsDraft: false,
        }}
        item={item}
        subitemComponent={ChildItemComponent}
        pathToDocPage={pathToDocPage}
        documentItemTypes={REQUIREMENT_ITEM_TYPES}
        canChangeVisibility={false}
        shouldRenderCreateInput={shouldRenderRequirementCreateInput}
      />

      {item.placement && (
        <BuildingPlacementBox
          project={project}
          docId={requirement.id}
          item={item}
        />
      )}
    </Stack>
  );
};

const createChildItemComponent =
  <T extends RequirementItem_ChildItemComponentFragment>(
    pathToDocPage: string,
    document: {
      viewerPermissions: PermissionName[];
    },
    docAgreement: Agreement
  ) =>
  (itemProps: SubitemComponentProps<T>) => {
    const [Data123Left, Data123Right] = React.useMemo(
      () => getData123(itemProps.item, itemProps.isPriceHidden),
      [itemProps.item, itemProps.isPriceHidden]
    );

    const RightIcons = React.useMemo(
      () => (
        <TreeRightIcons
          document={document}
          item={itemProps.item}
          isVisibleToOtherSide={itemProps.item.isVisibleToOtherSide}
        />
      ),
      [itemProps.item]
    );

    return (
      <BareTreeItem
        docAgreement={docAgreement}
        Data123Left={Data123Left}
        Data123Right={Data123Right}
        RightIcons={RightIcons}
        showPath={!itemProps.item.isDecisionOption}
        item={itemProps.item}
        isRootItem={itemProps.isRootItem}
        depth={itemProps.depth}
        isGreyedOut={itemProps.isGreyedOut}
        isHidden={itemProps.isHidden}
        isPriceHidden={itemProps.isPriceHidden}
        onClick={itemProps.onClick}
        subcontractPath={null}
        to={
          itemProps.to !== undefined
            ? itemProps.to
            : `${pathToDocPage}/items/${itemProps.item.id}`
        }
      />
    );
  };
